import React, { useEffect } from 'react';
import useHandleErrorFields from '../../../../hooks/useHandleErrorFields';
import SelectTwo from '../../../../components/SelectTwo';
import { ServiceDataForm } from '../../../../app/models/Service';

type SelectOpt = { id: number | string; name: string };

type Props = {
    service: ServiceDataForm;
    setService: (service: ServiceDataForm) => void;
    sectors?: SelectOpt[];
    itemCategories?: SelectOpt[];
    units?: SelectOpt[];
    taxes?: SelectOpt[];
    errorFields?: any;
};

const ServiceForm = ({
    service,
    setService,
    sectors = [],
    itemCategories = [],
    units = [],
    taxes = [],
    errorFields
}: Props) => {
    const { fieldErrorMessage, fieldHasError, onFocusRemove, setErrors } = useHandleErrorFields();

    useEffect(() => {
        setErrors(errorFields);
    }, [errorFields]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        const { name, value } = e.target;
        setService({ ...service, [name]: value });
    };

    const handleChangeSelectTwo = (name: keyof ServiceDataForm, value: number | string | null) => {
        setService({ ...service, [name]: (value ?? null) as any });
    };

    const mapOptions = (opts?: SelectOpt[]) =>
        (opts ?? []).map((o) => ({ value: o.id ?? null, label: String(o.name ?? '') }));

    return (
        <>
            <div className="mb-2 row">
                <div className="col-md-12">
                    <label className="col-form-label">Nombre <span className="text-danger">*</span></label>
                    <input
                        name="name"
                        id="name"
                        type="text"
                        className={`form-control ${fieldHasError('name')}`}
                        value={service.name ?? ''}
                        onChange={handleChange}
                        onFocus={() => onFocusRemove('name')}
                        placeholder="Ingrese nombre"
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('name')}
                    </div>
                </div>
            </div>
            <div className="mb-2 row">
                <div className="col-md-12">
                    <label className="col-form-label">Descripción</label>
                    <textarea
                        name="description"
                        id="description"
                        className={`form-control ${fieldHasError('description')}`}
                        value={service.description ?? ''}
                        onChange={handleChange}
                        onFocus={() => onFocusRemove('description')}
                        placeholder="Ingrese descripción"
                        rows={3}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('description')}
                    </div>
                </div>
            </div>
            <div className="mb-2 row">
                <div className="col-md-6">
                    <label className="col-form-label">Sector <span className="text-danger">*</span></label>
                    <SelectTwo
                        name="sector_id"
                        id="sector_id"
                        inputValue={service.sector_id ?? null}
                        options={mapOptions(sectors)}
                        hasError={fieldHasError('sector_id') !== ''}
                        onChange={(v: any) => handleChangeSelectTwo('sector_id', v?.value ?? null)}
                        placeholder="Seleccione sector"
                        onFocus={() => onFocusRemove('sector_id')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('sector_id')}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="col-form-label">Categoría <span className="text-danger">*</span></label>
                    <SelectTwo
                        name="item_category_id"
                        id="item_category_id"
                        inputValue={service.item_category_id ?? null}
                        options={mapOptions(itemCategories)}
                        hasError={fieldHasError('item_category_id') !== ''}
                        onChange={(v: any) => handleChangeSelectTwo('item_category_id', v?.value ?? null)}
                        placeholder="Seleccione categoría"
                        onFocus={() => onFocusRemove('item_category_id')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('item_category_id')}
                    </div>
                </div>
            </div>
            <div className="mb-2 row">
                <div className="col-md-12">
                    <label className="col-form-label">Unidad de medida <span className="text-danger">*</span></label>
                    <SelectTwo
                        name="unit_id"
                        id="unit_id"
                        inputValue={service.unit_id ?? null}
                        options={mapOptions(units)}
                        hasError={fieldHasError('unit_id') !== ''}
                        onChange={(v: any) => handleChangeSelectTwo('unit_id', v?.value ?? null)}
                        placeholder="Seleccione unidad"
                        onFocus={() => onFocusRemove('unit_id')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('unit_id')}
                    </div>
                </div>
            </div>
            <div className="mb-2 row">
                <div className="col-md-6">
                    <label className="col-form-label">Nombre de venta</label>
                    <input
                        name="sales_name"
                        id="sales_name"
                        type="text"
                        className={`form-control ${fieldHasError('sales_name')}`}
                        value={service.sales_name ?? ''}
                        onChange={handleChange}
                        onFocus={() => onFocusRemove('sales_name')}
                        placeholder="Nombre de venta"
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('sales_name')}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="col-form-label">Impuestos de venta</label>
                    <SelectTwo
                        name="sales_tax_id"
                        id="sales_tax_id"
                        inputValue={service.sales_tax_id ?? null}
                        options={mapOptions(taxes)}
                        hasError={fieldHasError('sales_tax_id') !== ''}
                        onChange={(v: any) => handleChangeSelectTwo('sales_tax_id', v?.value ?? null)}
                        placeholder="Seleccione impuesto"
                        onFocus={() => onFocusRemove('sales_tax_id')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('sales_tax_id')}
                    </div>
                </div>
            </div>
            <div className="mb-2 row">
                <div className="col-md-6">
                    <label className="col-form-label">Nombre de compra</label>
                    <input
                        name="purchase_name"
                        id="purchase_name"
                        type="text"
                        className={`form-control ${fieldHasError('purchase_name')}`}
                        value={service.purchase_name ?? ''}
                        onChange={handleChange}
                        onFocus={() => onFocusRemove('purchase_name')}
                        placeholder="Nombre de compra"
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('purchase_name')}
                    </div>
                </div>
                <div className="col-md-6">
                    <label className="col-form-label">Impuestos de compra</label>
                    <SelectTwo
                        name="purchase_tax_id"
                        id="purchase_tax_id"
                        inputValue={service.purchase_tax_id ?? null}
                        options={mapOptions(taxes)}
                        hasError={fieldHasError('purchase_tax_id') !== ''}
                        onChange={(v: any) => handleChangeSelectTwo('purchase_tax_id', v?.value ?? null)}
                        placeholder="Seleccione impuesto"
                        onFocus={() => onFocusRemove('purchase_tax_id')}
                    />
                    <div className="invalid-feedback" style={{ display: 'flex' }}>
                        {fieldErrorMessage('purchase_tax_id')}
                    </div>
                </div>
            </div>
        </>
    );
};

export default ServiceForm;
